import React, { createContext, FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { useShouldShowLimitedEvent } from '@/views/TicketCheckoutViews/ShowtimesView/components/Calendar/hooks'

interface LimitedDateModalContextType {
  isModalOpen: boolean
  openModal: () => void
  closeModal: () => void
  shouldShow: boolean
}

export const LimitedDateModalContext = createContext<LimitedDateModalContextType>({
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  shouldShow: false,
})

interface LimitedDateModalProviderProps {
  children: ReactNode
  theatricalSlug: string
  shouldOpenModal?: boolean
  initialIsOpen?: boolean
  isPremiereLocation?: boolean
}

export const LimitedDateModalProvider: FC<LimitedDateModalProviderProps> = ({
  children,
  theatricalSlug,
  shouldOpenModal = false,
  initialIsOpen = false,
  isPremiereLocation = false,
}) => {
  const { hasLimitedEvent: hasLimitedEventConfig } = useShouldShowLimitedEvent(theatricalSlug)
  const modalStateRef = useRef(initialIsOpen)
  const [isModalOpen, setIsModalOpen] = useState(initialIsOpen)

  useEffect(() => {
    modalStateRef.current = isModalOpen
  }, [isModalOpen])

  const hasSeenPromoRef = useRef(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storageKey = `has-seen-limited-date-promo-${theatricalSlug}`
      hasSeenPromoRef.current = getBooleanFromLocalStorage(storageKey) ?? false
    }
  }, [theatricalSlug])

  useEffect(() => {
    if (shouldOpenModal && hasLimitedEventConfig && !modalStateRef.current && !hasSeenPromoRef.current) {
      setIsModalOpen(true)
    }
  }, [shouldOpenModal, hasLimitedEventConfig])

  const openModal = useCallback(() => {
    if (hasLimitedEventConfig) {
      setIsModalOpen(true)
    }
  }, [hasLimitedEventConfig])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    if (typeof window !== 'undefined' && hasLimitedEventConfig) {
      writeToLocalStorage(`has-seen-limited-date-promo-${theatricalSlug}`, true)
      hasSeenPromoRef.current = true
    }
  }, [hasLimitedEventConfig, theatricalSlug])

  const shouldShow = hasLimitedEventConfig && !hasSeenPromoRef.current && isPremiereLocation

  return (
    <LimitedDateModalContext.Provider
      value={{
        isModalOpen,
        openModal,
        closeModal,
        shouldShow,
      }}
    >
      {children}
    </LimitedDateModalContext.Provider>
  )
}
